import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { utils as commonUtils } from '@finley-technologies/common';
import { progress } from '@finley-technologies/tape-builder';

import { ColumnInfo } from '@finley-technologies/tape-sanitizer/types/sanitizer';

import { colors } from 'packages/common-frontend/colors';

const FieldNameCell: React.FC<{
  fieldInfo: ColumnInfo;
}> = ({ fieldInfo }) => (
  <Td>
    {fieldInfo.name}
    {(() => {
      const optional = !fieldInfo.required;
      if (optional && fieldInfo.defaultValue) {
        return (
          <Text fontSize="xs">
            (optional, defaults to {fieldInfo.defaultValue})
          </Text>
        );
      }
      if (optional) {
        return <Text fontSize="xs">(optional)</Text>;
      }
      return <></>;
    })()}
  </Td>
);

export const ScrollableTable: React.FC<{
  children: React.ReactNode;
  numOfRows: number;
}> = ({ children, numOfRows }) => {
  const maxHeight = numOfRows * 45;
  return (
    <Box overflowY="scroll" maxHeight={`${maxHeight}px`}>
      {children}
    </Box>
  );
};

export const DataTapeErrorView: React.FC<{
  errors: progress.AggregatedError[];
  numOfRows: number;
}> = ({ errors, numOfRows }) => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <>
      {errors.map((error) => {
        switch (error.type) {
          case 'message':
            return (
              <Alert key={uuidv4()} status="error" mb="24px" borderRadius="6px">
                <AlertIcon />
                <AlertDescription fontSize="14px" fontWeight="bold">
                  {error.message}
                </AlertDescription>
              </Alert>
            );
          case 'dataValidation':
            return (
              <Box key={error.dataTapeSlug}>
                <Alert
                  status="error"
                  mb={isOpen ? '24px' : '0px'}
                  borderRadius="6px"
                >
                  <AlertIcon />
                  <AlertDescription fontSize="14px" fontWeight="bold">
                    {`There are invalid ${commonUtils.toTitleCase(
                      error.dataTapeSlug,
                    )} fields.`}
                    <Button
                      size="sm"
                      color="blue.300"
                      variant="link"
                      onClick={onToggle}
                      ml="8px"
                      _focus={{ boxShadow: 'none' }}
                    >
                      {isOpen ? 'Hide Errors' : 'View Errors'}
                    </Button>
                  </AlertDescription>
                </Alert>
                <Collapse in={isOpen} animateOpacity>
                  <Heading as="h5" size="sm" mb="24px">
                    Invalid {commonUtils.toTitleCase(error.dataTapeSlug)} fields
                  </Heading>
                  {!!error.xlsxExtractionErrors.length && (
                    <ScrollableTable numOfRows={numOfRows}>
                      <Table
                        mb="24px"
                        border="1px"
                        borderColor={colors.gray[100]}
                      >
                        <Thead>
                          <Tr>
                            <Th>Sheet name</Th>
                            <Th>Cell</Th>
                            <Th>Field name</Th>
                            <Th>Error</Th>
                          </Tr>
                        </Thead>
                        <Tbody fontSize="sm">
                          {error.xlsxExtractionErrors.map(
                            ({ sheetName, cell, fieldName, message }) => (
                              <Tr key={`${sheetName},${cell},${fieldName}`}>
                                <Td>{sheetName}</Td>
                                <Td>{cell}</Td>
                                <Td>{fieldName}</Td>
                                <Td>{message}</Td>
                              </Tr>
                            ),
                          )}
                        </Tbody>
                      </Table>
                    </ScrollableTable>
                  )}
                  {!!error.xlsxCellErrors.length && (
                    <ScrollableTable numOfRows={numOfRows}>
                      <Table
                        mb="24px"
                        border="1px"
                        borderColor={colors.gray[100]}
                      >
                        <Thead>
                          <Tr>
                            <Th>Sheet name</Th>
                            <Th>
                              {error.xlsxCellErrors[0].cell === undefined
                                ? 'Row'
                                : 'Cell'}
                            </Th>
                            <Th>Field name</Th>
                            <Th>Entered value</Th>
                            <Th>Expected type</Th>
                            <Th>Examples</Th>
                          </Tr>
                        </Thead>
                        <Tbody fontSize="sm">
                          {error.xlsxCellErrors.map(
                            ({
                              sheetName,
                              fieldInfo,
                              cell,
                              rowNumber,
                              enteredValue,
                            }) => (
                              <Tr
                                key={`${sheetName},${cell},${rowNumber},${fieldInfo.name}`}
                              >
                                <Td>{sheetName}</Td>
                                <Td>{cell ?? rowNumber}</Td>
                                <FieldNameCell fieldInfo={fieldInfo} />
                                <Td>{enteredValue}</Td>
                                <Td>{fieldInfo.type}</Td>
                                <Td>{fieldInfo.examples.join(', ')}</Td>
                              </Tr>
                            ),
                          )}
                        </Tbody>
                      </Table>
                    </ScrollableTable>
                  )}
                  {!!error.xlsxColumnErrors.length && (
                    <ScrollableTable numOfRows={numOfRows}>
                      <Table
                        mb="24px"
                        border="1px"
                        borderColor={colors.gray[100]}
                      >
                        <Thead>
                          <Tr>
                            <Th>Sheet name</Th>
                            <Th>Field name</Th>
                            <Th>Expected type</Th>
                            <Th>Examples</Th>
                          </Tr>
                        </Thead>
                        <Tbody fontSize="sm">
                          {error.xlsxColumnErrors.map(
                            ({ sheetName, fieldInfo }) => (
                              <Tr key={`${sheetName},${fieldInfo.name}`}>
                                <Td>{sheetName}</Td>
                                <FieldNameCell fieldInfo={fieldInfo} />
                                <Td>{fieldInfo.type}</Td>
                                <Td>{fieldInfo.examples.join(', ')}</Td>
                              </Tr>
                            ),
                          )}
                        </Tbody>
                      </Table>
                    </ScrollableTable>
                  )}
                  {!!error.csvFieldErrors.length && (
                    <ScrollableTable numOfRows={numOfRows}>
                      <Table
                        mb="24px"
                        border="1px"
                        borderColor={colors.gray[100]}
                      >
                        <Thead>
                          <Tr>
                            <Th>Field name</Th>
                            <Th>Row</Th>
                            <Th>Entered value</Th>
                            <Th>Expected type</Th>
                            <Th>Examples</Th>
                          </Tr>
                        </Thead>
                        <Tbody fontSize="sm">
                          {error.csvFieldErrors.map(
                            ({ fieldInfo, rowNumber, enteredValue }) => (
                              <Tr key={`${rowNumber},${fieldInfo.name}`}>
                                <FieldNameCell fieldInfo={fieldInfo} />
                                <Td>{rowNumber}</Td>
                                <Td>{enteredValue}</Td>
                                <Td>{fieldInfo.type}</Td>
                                <Td>{fieldInfo.examples.join(', ')}</Td>
                              </Tr>
                            ),
                          )}
                        </Tbody>
                      </Table>
                    </ScrollableTable>
                  )}
                  {!!error.csvColumnErrors.length && (
                    <ScrollableTable numOfRows={numOfRows}>
                      <Table
                        mb="24px"
                        border="1px"
                        borderColor={colors.gray[100]}
                      >
                        <Thead>
                          <Tr>
                            <Th>Field name</Th>
                            <Th>Expected type</Th>
                            <Th>Examples</Th>
                          </Tr>
                        </Thead>
                        <Tbody fontSize="sm">
                          {error.csvColumnErrors.map(({ fieldInfo }) => (
                            <Tr key={fieldInfo.name}>
                              <FieldNameCell fieldInfo={fieldInfo} />
                              <Td>{fieldInfo.type}</Td>
                              <Td>{fieldInfo.examples.join(', ')}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </ScrollableTable>
                  )}
                </Collapse>
              </Box>
            );
          default:
            return <></>;
        }
      })}
    </>
  );
};
